<div class="reading-page doc-page">
  <div id="head" class="section-header">
    <h2 class="section-title">LangMi Terms of Use</h2>
  </div>
  <app-policy-card [policyBody]="body">
    <!-- Body -->
    <ng-template #body>
      <div class="policy-content">
        <p><strong>Last updated: 25 September 2026</strong></p>
        <p>These Terms of Use ("Terms") form an agreement between you and <strong>Codigrate OÜ</strong>, a private limited company
          registered in Estonia (<a [href]="environmentCommon.company.website">{{ environmentCommon.company.website }}</a>) for the use of the
          <strong>LangMi</strong> mobile application ("the App"). By downloading or using the App, you accept these
          Terms. If you do not accept them, please do not use the App. For our website, see our
          <a routerLink="{{URLS.terms}}">Terms</a>.</p>

        <p><strong>Licence</strong></p>
        <p>We grant you a personal, non-exclusive, non-transferable and revocable licence to use the App on devices you
          own or control, as permitted by the App Store Terms of Service. You may not copy, sell, rent, sub-licence,
          reverse-engineer or redistribute the App or its story, audio and illustration content, except where such a
          restriction is prohibited by law.</p>

        <p><strong>Subscriptions</strong></p>
        <p>LangMi offers a free tier and an optional auto-renewing subscription, <strong>LangMi Pro</strong>
          (monthly or yearly). Pro unlocks the Pro stories (seasons beyond the free ones), Learn together with a teacher
          and the teacher tools, learning several languages side by side with one mistakes journal across them, and the
          premium theme collection; new Pro seasons are included on the day they are released. The following applies to
          Pro:</p>
        <p>- Payment is charged to your Apple Account at confirmation of purchase.</p>
        <p>- The subscription <strong>renews automatically</strong> unless it is cancelled at least
          <strong>24 hours before the end of the current period</strong>.</p>
        <p>- Your Apple Account is charged for renewal within 24 hours before the end of the current period, at the
          price of the plan you selected.</p>
        <p>- You can manage or cancel your subscription in your Apple Account settings on your device, at any time
          after purchase.</p>
        <p>- Where a free trial is offered, any unused portion of it is forfeited when you buy a subscription.</p>
        <p>- Prices are shown in the App before purchase and may vary by country. We may change prices for future
          periods; you will be told in advance where the law requires it.</p>

        <p><strong>Refunds</strong></p>
        <p>Purchases are made through Apple, so refunds are handled by Apple under its own policy and not by Codigrate.
          Refund requests can be made at
          <a href="https://reportaproblem.apple.com" target="_blank" rel="noopener">reportaproblem.apple.com</a>.</p>

        <p><strong>Accounts</strong></p>
        <p>You do not need an account to learn with LangMi. You may optionally sign in with your Apple Account (Sign in
          with Apple) from onboarding or from <strong>Settings › Account</strong>. We ask Apple only for your e-mail
          address, which you may hide behind Apple's private relay; we do not ask for your name. The name that other
          people see in LangMi is the name you typed in onboarding, followed by a number that makes it unique. You are
          responsible for keeping your device and your Apple Account secure, and for everything done through your
          LangMi account. You can sign out or delete your account at any time from Settings › Account.</p>

        <p><strong>Your content and your progress</strong></p>
        <p>Your learning progress is stored on your device. If you sign in, a copy is also kept on our servers so that
          you can continue where you left off on another device signed in with the same Apple Account. Deleting the
          App deletes the copy on that device; the server copy stays until you delete your account, and it comes back
          when you sign in again. If you never sign in, we cannot restore progress you lose by deleting the App,
          resetting your journey or losing your device. Your Pro subscription is tied to your Apple Account, not to your
          progress, and can be restored from the Pro screen on any device signed in with the same Apple Account.</p>

        <p><strong>Learn together and the teacher profile</strong></p>
        <p>LangMi Pro lets a student and a teacher share one story session: the student generates a short code, the
          teacher enters it, and — once the student accepts — the two of them play the same story together: the
          teacher can point, highlight the chapter's grammar and leave notes. LangMi only lets you play together; it
          does not share, stream or record your device screen. Both sides need an active Pro subscription. As a student you
          decide whom to accept, you can end a session at any time from the pill at the top of the screen, and you see
          the teacher's display name. Codigrate only carries the session between the two devices; it does not choose,
          vet, employ or pay teachers, and any lesson arrangement is between the student and the teacher. Teachers must
          also accept the <a routerLink="{{URLS.langmiTeacherTerms}}">LangMi Teacher Terms</a> before they can enable
          the teacher profile, and those terms apply to them in addition to these Terms.</p>

        <p><strong>Acceptable use</strong></p>
        <p>You agree not to use the App unlawfully, not to interfere with its operation or security, not to attempt to
          gain access to parts of the App you have not paid for, and — in a shared session — to treat the other person
          with respect and to use notes only for learning.</p>

        <p><strong>Educational purpose and disclaimer</strong></p>
        <p>LangMi is a language-learning aid. Its stories, characters, places and dialogues are written for teaching
          purposes and are partly fictional; they are not travel, legal or professional advice. We do not guarantee any
          particular learning outcome, exam result or level of fluency.</p>
        <p>The App is provided "as is" and "as available". To the fullest extent permitted by law, we disclaim all
          implied warranties, including fitness for a particular purpose, and we do not warrant that the App will be
          uninterrupted or error-free.</p>

        <p><strong>Limitation of liability</strong></p>
        <p>To the fullest extent permitted by law, Codigrate is not liable for indirect, incidental or consequential
          loss arising from your use of the App. Nothing in these Terms limits liability that cannot be limited by law,
          including liability for death or personal injury caused by negligence, or for fraud. Your statutory consumer
          rights are not affected.</p>

        <p><strong>Changes to the App and to these Terms</strong></p>
        <p>We may add, change or withdraw features, seasons or content over time. We may also update these Terms; the
          date at the top of this page shows when they last changed. If a change is significant, we will make it clear
          inside the App. Continuing to use the App after a change means you accept the updated Terms.</p>

        <p><strong>Termination</strong></p>
        <p>You may stop using the App at any time by deleting it, and you may delete your account from Settings ›
          Account. We may suspend or end your licence, your account or your teacher profile if you materially breach
          these Terms or the Teacher Terms.</p>

        <p><strong>Third parties</strong></p>
        <p>Apple is not a party to these Terms and is not responsible for the App or its content. Apple has no
          obligation to provide support for the App. Apple and its subsidiaries are third-party beneficiaries of these
          Terms and may enforce them against you.</p>

        <p><strong>Governing law</strong></p>
        <p>These Terms are governed by the laws of Estonia. If you are a consumer in the European Union or the United
          Kingdom, you also keep the protection of the mandatory rules of the country where you live, and you may bring a
          claim in its courts. Disputes are first raised with us by e-mail; we try to settle them amicably.</p>

        <p><strong>Privacy</strong></p>
        <p>How the App handles information is described in the
          <a routerLink="{{URLS.langmiPrivacy}}">LangMi Privacy Policy</a>.</p>

        <p><strong>Contact</strong></p>
        <p>Questions about these Terms can be sent to
          <a [href]="'mailto:' + environmentCommon.company.email">{{ environmentCommon.company.email }}</a>.</p>
      </div>
    </ng-template>
  </app-policy-card>
</div>
